// =============================
// Imports
// =============================

import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
  downloadArchives as downloadArchivesBase,
  downloadCopyright as downloadCopyrightBase,
  duplicatePlaylist as duplicatePlaylistBase,
} from '../../../../store/actions/EntitiesActions';
import { getMedia, pause, play } from '../../../../store/actions/PlayerActions';
import { duplicateUserPlaylist as duplicateUserPlaylistBase } from '../../../../store/actions/UserPlaylistsActions';

import AddToPlaylist from '../../addToPlaylist';
import DownloadArchiveButton from '../../downloadArchiveButton';
import Link from '../../link';
import ShareButton from '../../shareButton';

import { getTitle, getUrl } from '../../../../helpers/entity';
import * as pth from '../../../../helpers/proptypes';

import * as playerStates from '../../../../store/constants/PlayerStates';

import {
  Actions,
  AddToPlaylistIcon,
  DownloadIcon,
  DropdownButton,
  PauseButton,
  PlayButton,
  ShareIcon,
  StyledTooltip,
  ThreeDotIcon,
} from './styles';

// =============================
// Component
// =============================

class EntityActions extends PureComponent {
  static displayName = 'EntityActionsDesktop';

  static propTypes = {
    additionalActions: PropTypes.node,
    doc: PropTypes.oneOfType([pth.catalog, pth.album, pth.artist, pth.playlist, pth.userplaylist]),
    downloadArchives: PropTypes.func.isRequired,
    downloadCopyright: PropTypes.func.isRequired,
    /** Dropdowns placement. */
    dropdownsPlacement: pth.dropdownPlacement,
    duplicatePlaylist: PropTypes.func.isRequired,
    duplicateUserPlaylist: PropTypes.func.isRequired,
    hasTracks: PropTypes.bool.isRequired,
    isLogged: PropTypes.bool.isRequired,
    onGetMedia: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
    onPlay: PropTypes.func.isRequired,
    options: PropTypes.shape({
      addToPlaylist: PropTypes.bool,
      downloadArchive: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      share: PropTypes.bool,
      duplicatePlaylist: PropTypes.bool,
      downloadCopyright: PropTypes.bool,
    }).isRequired,
    playerContext: PropTypes.string.isRequired,
    playerStore: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    pitchToken: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    type: PropTypes.oneOf([
      'catalog',
      'album',
      'artist',
      'playlist',
      'pitch',
      'userplaylist',
      'user_favorites',
      'user_recents',
    ]).isRequired,
  };

  static defaultProps = {
    additionalActions: null,
    doc: null,
    dropdownsPlacement: 'bottom',
  };

  getApiContext = () => {
    const { type } = this.props;

    const contextMap = {
      album: 'album',
      playlist: 'playlist',
      userplaylist: 'user_playlist',
      pitch: 'pitch',
    };

    return contextMap[type];
  };

  isPlaying = () => {
    const { doc, playerContext, playerStore } = this.props;

    const contextId = doc ? doc.id : null;

    return !!(
      _get(playerStore, 'contextName', null) === playerContext
      && _get(playerStore, 'contextId', null) === contextId
      && _get(playerStore, 'playerState') === playerStates.PLAYING
    );
  };

  isPaused = () => {
    const { playerContext, playerStore } = this.props;

    return !!(
      _get(playerStore, 'contextName', null) === playerContext
      && _get(playerStore, 'playerState') === playerStates.PAUSED
    );
  }

  handleOnPlay = () => {
    const { doc, onGetMedia, onPause, onPlay, playerContext } = this.props;

    // Pause if a track in being played within the context
    if (this.isPlaying()) {
      return onPause();
    }

    // If the current context is same as prop and the state is paused,
    // we are resuming the previous track within current context
    if (this.isPaused()) {
      return onPlay();
    }

    return onGetMedia(null, playerContext, doc ? doc.id : null);
  };

  handleDownloadArchives = (quality = 'original', withVersions = false) => {
    const { doc, downloadArchives } = this.props;

    downloadArchives(this.getApiContext(), [doc.id], quality, withVersions);
  };

  handleDownloadCopyright = () => {
    const { doc, downloadCopyright, type } = this.props;

    downloadCopyright(this.getApiContext(), doc.id, getTitle(`${type}s`)(doc));
  };

  handleDuplicatePlaylist = () => {
    const { doc, duplicatePlaylist, duplicateUserPlaylist, t, type } = this.props;

    const nextName = `${getTitle(`${type}s`)(doc)} (${t(
      'components:entity_actions.duplicate_suffix',
    )})`;

    if (type === 'userplaylist') {
      duplicateUserPlaylist(doc.id, nextName);
    } else if (type === 'playlist') {
      duplicatePlaylist(doc.id, nextName);
    }
  };

  render() {
    const {
      additionalActions,
      doc,
      dropdownsPlacement,
      hasTracks,
      isLogged,
      options,
      pitchToken,
      t,
      type,
    } = this.props;

    const hasDropdownAction = options.duplicatePlaylist || options.downloadCopyright;

    return (
      <Actions>
        {hasTracks && (
          <Fragment>
            {this.isPlaying() ? (
              <PauseButton onClick={this.handleOnPlay}>
                {t('components:entity_actions.pause')}
              </PauseButton>
            ) : (
              <PlayButton onClick={this.handleOnPlay}>
                {t('components:entity_actions.play')}
              </PlayButton>
            )}
          </Fragment>
        )}

        {isLogged && hasTracks && (
          <Fragment>
            {doc && options.addToPlaylist && (
              <AddToPlaylist
                placement={dropdownsPlacement}
                type={type}
                itemId={doc.id}
                togglerElement={(
                  <StyledTooltip content={t('components:entity_actions.add_to_playlist_tooltip')}>
                    <AddToPlaylistIcon />
                  </StyledTooltip>
                )}
              />
            )}
            {doc && options.downloadArchive && (
              <DownloadArchiveButton
                placement={dropdownsPlacement}
                downloadLowQ={() => this.handleDownloadArchives('hd_mp3')}
                downloadHighQ={() => this.handleDownloadArchives('original')}
                downloadLowQWithVersions={() => this.handleDownloadArchives('hd_mp3', true)}
                downloadHighQWithVersions={() => this.handleDownloadArchives('original', true)}
                togglerElement={(
                  <StyledTooltip content={t('components:entity_actions.download_archive_tooltip')}>
                    <DownloadIcon />
                  </StyledTooltip>
                )}
              />
            )}
          </Fragment>
        )}
        {!isLogged && hasTracks && (
          <Fragment>
            {doc && options.addToPlaylist && (
              <StyledTooltip content={t('components:entity_actions.add_to_playlist_tooltip')}>
                <Link route="/login" noHover>
                  <AddToPlaylistIcon />
                </Link>
              </StyledTooltip>
            )}
            {doc && options.downloadArchive && !pitchToken && (
              <StyledTooltip content={t('components:entity_actions.download_archive_tooltip')}>
                <Link route="/login" noHover>
                  <DownloadIcon />
                </Link>
              </StyledTooltip>
            )}
            {doc && options.downloadArchive && !!pitchToken && (
              <DownloadArchiveButton
                placement={dropdownsPlacement}
                downloadLowQ={() => this.handleDownloadArchives('hd_mp3')}
                downloadHighQ={() => this.handleDownloadArchives('original')}
                downloadLowQWithVersions={() => this.handleDownloadArchives('hd_mp3', true)}
                downloadHighQWithVersions={() => this.handleDownloadArchives('original', true)}
                togglerElement={(
                  <StyledTooltip content={t('components:entity_actions.download_archive_tooltip')}>
                    <DownloadIcon />
                  </StyledTooltip>
                )}
              />
            )}
          </Fragment>
        )}
        {doc && options.share && (
          <ShareButton
            placement={dropdownsPlacement}
            url={getUrl(`${type}s`)(doc)}
            text={getTitle(`${type}s`)(doc)}
            togglerElement={(
              <StyledTooltip content={t('components:entity_actions.share_tooltip')}>
                <ShareIcon />
              </StyledTooltip>
            )}
          />
        )}
        {doc && hasDropdownAction && hasTracks && (isLogged || pitchToken) && (
          <DropdownButton
            placement={dropdownsPlacement}
            options={[
              ...(options.downloadCopyright
                ? [
                  {
                    action: this.handleDownloadCopyright,
                    label: t('components:entity_actions.download_copyright'),
                    name: 'download_copyright',
                  },
                ]
                : []),
              ...(options.duplicatePlaylist
                ? [
                  {
                    action: this.handleDuplicatePlaylist,
                    label: t('components:entity_actions.duplicate_playlist'),
                    name: 'duplicate_playlist',
                  },
                ]
                : []),
            ]}
            togglerElement={(
              <StyledTooltip content={t('components:entity_actions.other_actions_tooltip')}>
                <ThreeDotIcon />
              </StyledTooltip>
            )}
          />
        )}
        {doc && hasDropdownAction && hasTracks && !isLogged && !pitchToken && (
          <StyledTooltip content={t('components:entity_actions.other_actions_tooltip')}>
            <Link route="/login" noHover>
              <ThreeDotIcon />
            </Link>
          </StyledTooltip>
        )}
        {additionalActions}
      </Actions>
    );
  }
}

function mapStateToProps({ user, player }) {
  return {
    isLogged: user.isLogged,
    playerStore: player,
    pitchToken: user.pitchToken,
  };
}

export default compose(
  connect(mapStateToProps, {
    downloadArchives: downloadArchivesBase,
    downloadCopyright: downloadCopyrightBase,
    duplicateUserPlaylist: duplicateUserPlaylistBase,
    duplicatePlaylist: duplicatePlaylistBase,
    onGetMedia: getMedia,
    onPause: pause,
    onPlay: play,
  }),
)(EntityActions);
